import { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import { GetPromoCodeProps, getPromoCodes } from '~/services/PromoService';
import UsersService from '~/services/UsersService';

type UsePromoCodeProps = {
  initialFilters: GetPromoCodeProps;
};

const queryParamNames = {
  page: 'page',
  limit: 'limit',
  name: 'name',
  emailAddress: 'emailAddress',
  caseId: 'caseId',
  expiresAfter: 'expiresAfter',
  noChildPromos: 'noChildPromos',
  onlyChildPromos: 'onlyChildPromos',
  excludeReferralPromos: 'noReferralPromos',
  userIdInteracted: 'userIdInteracted',
  deptTag: 'deptTag',
  categoryTag: 'categoryTag',
  subCategoryTag: 'subCategoryTag',
  dev: 'dev',
  brand: 'brand',
};

function usePromoCodes({ initialFilters }: UsePromoCodeProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [promoCodes, setPromoCodes] = useState(null);
  const [total, setTotal] = useState(0);
  const [promoWarnings, setPromoWarnings] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [filters, setFilters] = useState<GetPromoCodeProps>(initialFilters);

  const fetchData = useCallback(
    async (filters) => {
      try {
        setLoading(true);
        const promos = await getPromoCodes(filters);

        const userIds = [
          ...new Set(
            promos.result
              .map((promo) => promo.added_by)
              .concat(promos.result.map((promo) => promo.updated_by))
              .concat(promos.result.map((promo) => promo.modified_by))
              .concat(promos.result.map((promo) => promo.approved_by)),
          ),
        ].filter(Boolean);
        const users = await UsersService.getUsersSummaryByIds(userIds, initialFilters.brand).catch((error) => {
          console.error('Failed to fetch users: ', error);
          return new Map();
        });

        setPromoCodes(
          promos.result.map((promo) => ({
            ...promo,
            addedBy: users.get(promo.added_by),
            modifiedBy: users.get(promo.modified_by),
            approvedBy: users.get(promo.approved_by),
          })),
        );
        setTotal(promos.total);
      } catch (error) {
        enqueueSnackbar('Failed to fetch promo codes: ' + error.message, { variant: 'error' });
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar],
  );

  return { promoCodes, promoWarnings, isLoading, fetchData, filters, setFilters, total };
}

export { usePromoCodes, queryParamNames };
